// Servicio de WhatsApp que funciona con WhatsApp Web
class WhatsAppService {
  constructor() {
    this.isConnected = false;
    this.baseURL = "https://web.whatsapp.com/send";
  }

  // Verificar si WhatsApp Web está conectado
  async checkConnection() {
    try {
      // Simulamos verificación - en un entorno real podrías hacer una llamada al backend
      const connected = localStorage.getItem("whatsapp_connected") === "true";
      this.isConnected = connected;
      return {
        connected,
        message: connected
          ? "WhatsApp Web conectado"
          : "WhatsApp Web no conectado",
      };
    } catch (error) {
      return {
        connected: false,
        message: "Error verificando conexión",
      };
    }
  }

  // Marcar como conectado
  setConnected(status = true) {
    this.isConnected = status;
    localStorage.setItem("whatsapp_connected", status.toString());
  }

  // Formatear número de teléfono
  formatNumber(number) {
    // Eliminar todo excepto números
    let cleaned = number.replace(/\D/g, "");

    // Si es número dominicano (10 dígitos comenzando con 8), agregar código de país
    if (cleaned.length === 10 && cleaned.startsWith("8")) {
      cleaned = "1" + cleaned;
    }

    return cleaned;
  }

  // Enviar mensaje individual usando WhatsApp Web
  async sendMessage(number, message) {
    try {
      if (!this.isConnected) {
        throw new Error(
          "WhatsApp no está conectado. Vincule primero su dispositivo."
        );
      }

      const formattedNumber = this.formatNumber(number);
      const encodedMessage = encodeURIComponent(message);
      const whatsappURL = `${this.baseURL}?phone=${formattedNumber}&text=${encodedMessage}`;

      // Abrir WhatsApp Web con el mensaje pre-llenado
      const popup = window.open(
        whatsappURL,
        "whatsapp",
        "width=1000,height=600,scrollbars=yes,resizable=yes"
      );

      if (!popup) {
        throw new Error(
          "Por favor, permita ventanas emergentes para enviar mensajes via WhatsApp"
        );
      }

      // Registrar el intento de envío
      this.logMessage(number, message, "enviado");

      return {
        success: true,
        message:
          "Mensaje abierto en WhatsApp Web. Complete el envío manualmente.",
        method: "whatsapp_web",
      };
    } catch (error) {
      this.logMessage(number, message, "fallido", error.message);
      throw error;
    }
  }

  // Enviar mensaje masivo
  async sendBulkMessages(contacts, message) {
    const results = {
      total: contacts.length,
      sent: 0,
      failed: 0,
      errors: [],
    };

    for (let i = 0; i < contacts.length; i++) {
      const contact = contacts[i];

      try {
        await this.sendMessage(
          contact.numero,
          message.replace("{nombre}", contact.nombre)
        );
        results.sent++;

        // Pausa entre mensajes para no sobrecargar
        if (i < contacts.length - 1) {
          await this.delay(2000); // 2 segundos entre mensajes
        }
      } catch (error) {
        results.failed++;
        results.errors.push({
          contact: contact.nombre,
          number: contact.numero,
          error: error.message,
        });
      }
    }

    return results;
  }

  // Generar recordatorios automáticos
  async generateReminders() {
    try {
      // Simular generación de recordatorios
      const reminders = [
        {
          cliente: "Juan Pérez",
          numero: "8091234567",
          mensaje:
            "Hola Juan, le recordamos que su cuota vence mañana. Gracias.",
        },
        {
          cliente: "María García",
          numero: "8097654321",
          mensaje:
            "Estimada María, su pago está próximo a vencer. Por favor, realice su pago.",
        },
      ];

      const results = await this.sendBulkMessages(
        reminders.map((r) => ({ nombre: r.cliente, numero: r.numero })),
        "{nombre}, le recordamos sobre su próximo pago."
      );

      return {
        mensajes_creados: reminders.length,
        enviados: results.sent,
        fallidos: results.failed,
        detalles: results,
      };
    } catch (error) {
      throw new Error("Error generando recordatorios: " + error.message);
    }
  }

  // Enviar mensaje de prueba
  async sendTestMessage(number) {
    const testMessage = `¡Hola! 👋

Este es un mensaje de prueba del Sistema de Préstamos KR-V2.

✅ Su WhatsApp está configurado correctamente.
📅 Fecha: ${new Date().toLocaleDateString("es-DO")}
⏰ Hora: ${new Date().toLocaleTimeString("es-DO")}

¡Gracias por usar nuestro sistema!`;

    return await this.sendMessage(number, testMessage);
  }

  // Registrar mensaje en localStorage (simulando base de datos)
  logMessage(number, message, status, error = null) {
    try {
      const logs = JSON.parse(localStorage.getItem("whatsapp_logs") || "[]");

      const logEntry = {
        id: Date.now(),
        numero: number,
        mensaje:
          message.substring(0, 100) + (message.length > 100 ? "..." : ""),
        estado: status,
        error: error,
        fecha: new Date().toISOString(),
        metodo: "whatsapp_web",
      };

      logs.unshift(logEntry);

      // Mantener solo los últimos 100 registros
      if (logs.length > 100) {
        logs.splice(100);
      }

      localStorage.setItem("whatsapp_logs", JSON.stringify(logs));
    } catch (error) {
      console.error("Error guardando log:", error);
    }
  }

  // Obtener historial de mensajes
  getMessageHistory() {
    try {
      const logs = JSON.parse(localStorage.getItem("whatsapp_logs") || "[]");
      return logs;
    } catch (error) {
      console.error("Error obteniendo historial:", error);
      return [];
    }
  }

  // Obtener estadísticas
  getStatistics() {
    try {
      const logs = this.getMessageHistory();
      const today = new Date().toDateString();
      const thisWeek = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);

      const todayLogs = logs.filter(
        (log) => new Date(log.fecha).toDateString() === today
      );
      const weekLogs = logs.filter((log) => new Date(log.fecha) >= thisWeek);

      return {
        hoy: {
          enviados: todayLogs.filter((log) => log.estado === "enviado").length,
          fallidos: todayLogs.filter((log) => log.estado === "fallido").length,
          pendientes: 0,
        },
        semana: {
          enviados: weekLogs.filter((log) => log.estado === "enviado").length,
          fallidos: weekLogs.filter((log) => log.estado === "fallido").length,
        },
        total: logs.length,
      };
    } catch (error) {
      console.error("Error calculando estadísticas:", error);
      return {
        hoy: { enviados: 0, fallidos: 0, pendientes: 0 },
        semana: { enviados: 0, fallidos: 0 },
        total: 0,
      };
    }
  }

  // Función auxiliar para pausas
  delay(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
}

// Crear instancia global
const whatsappWebService = new WhatsAppService();

export default whatsappWebService;
